'use client'

import FormSelectInput from '@/components/forms/inputs/form-select-input'
import DiscordIcon from '@/components/icons/logos/discord'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { CheckCircle, CircleX } from 'lucide-react'
import { signOut } from 'next-auth/react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { useEffect, useState } from 'react'
import useSWRImmutable from 'swr/immutable'
import getLinkedStatusOfServers from '../../../actions/get-linked-status-of-servers'
import getUserDiscordServers from '../../../actions/get-user-discord-servers'
import ConnectDiscordDialog from './connect-discord-dialog'
import DiscordRoleReauthDialog from './discord-role-reauth-dialog'

export default function CommunityDiscordServerInput({
  form,
  originalValue,
  className,
}) {
  const [openReauthDiscord, setOpenReauthDiscord] = useState(false)
  const [openConnectDiscord, setOpenConnectDiscord] = useState(false)
  const [submitCount, setSubmitCount] = useState(0)

  const [discordAuthError, setDiscordAuthError] = useState('')

  const currentValue = form.watch('discordServerId')

  const {
    data: result,
    isLoading,
    error,
  } = useSWRImmutable({ filterAdminServers: true }, getUserDiscordServers)

  const discordServers = result?.servers || []
  const status = result?.status

  const showDiscordReauthPrompt = () => {
    setDiscordAuthError(<DiscordReauthMessage />)

    if (localStorage.getItem('discordScopesWarningViewed') !== 'true') {
      setOpenReauthDiscord(true)
      localStorage.setItem('discordScopesWarningViewed', 'true')
    }
  }

  useEffect(() => {
    if (currentValue === 'n/a') {
      form.setValue('discordServerId', null, { shouldDirty: true })
      form.setValue('notificationChannelId', null, { shouldDirty: true })
    }
  }, [currentValue, form])

  useEffect(() => {
    if (form.formState.isSubmitSuccessful) {
      // Only change if the discordServerId value was changed
      if (form.getValues('discordServerId') !== originalValue) {
        setSubmitCount(submitCount + 1)
      }
    }
  }, [form.formState])

  useEffect(() => {
    // Handle status-based errors (no Discord account)
    if (status === 'NO_DISCORD_ACCOUNT') {
      setDiscordAuthError(<DiscordConnectMessage />)

      if (localStorage.getItem('discordAccountWarningViewed') !== 'true') {
        setOpenConnectDiscord(true)
        localStorage.setItem('discordAccountWarningViewed', 'true')
      }
      return
    }

    if (status === 'DISCORD_REAUTH_REQUIRED') {
      showDiscordReauthPrompt()
      return
    }

    // Handle thrown errors (legacy auth errors, network/API failures)
    if (!error) {
      setDiscordAuthError('')
      return
    }

    if (
      error.message === 'Missing guilds scope' ||
      error.message === 'Unauthorized'
    ) {
      showDiscordReauthPrompt()
    }
  }, [status, error])

  const [selectOptions, setSelectOptions] = useState([])

  useEffect(() => {
    async function fetchAndSetOptions() {
      if (!discordServers || !discordServers.length) {
        return
      }

      const serversWithLinkedStatus =
        await getLinkedStatusOfServers(discordServers)

      const options = serversWithLinkedStatus
        .map((server) => ({
          value: server.id,
          label: `${server.name} ${
            server.linked && server.id !== originalValue ? ' (Linked)' : ''
          }`,
          disabled: server.linked && server.id !== originalValue,
        }))
        .sort((a, b) => a.label.localeCompare(b.label))

      setSelectOptions(options)
    }

    fetchAndSetOptions()
  }, [discordServers, originalValue, submitCount])

  const isConnected = originalValue && originalValue === currentValue

  return (
    <>
      <div className="flex flex-col gap-1">
        <FormSelectInput
          name="discordServerId"
          options={[...selectOptions, { value: 'n/a', label: 'None' }]}
          emptyOption={{
            value: 'n/a',
            label: 'No Discord servers found',
            disabled: true,
          }}
          disabled={isLoading || !!discordAuthError}
          placeholder="Select a Discord server"
          label="Connected Server"
          labelPrefix={
            <DiscordIcon className="h-6 w-6 fill-shades-0 stroke-shades-0" />
          }
          btnEndText={
            !isLoading && currentValue && currentValue !== 'n/a' ? (
              <div className="flex items-center gap-1 text-nowrap font-medium">
                {isConnected ? (
                  <CheckCircle className="h-5 w-5 stroke-2 text-success-500" />
                ) : (
                  <CircleX className="h-5 w-5 stroke-2 text-error-500" />
                )}

                <span
                  className={cn('text-sm', {
                    'text-success-500': isConnected,
                    'text-error-500': !isConnected,
                  })}
                >
                  {isConnected ? 'Connected' : 'Save to connect'}
                </span>
              </div>
            ) : null
          }
          className="w-full"
          btnClassName={cn('w-full justify-between overflow-hidden', className)}
          {...form}
        />

        {discordAuthError}
      </div>

      <DiscordRoleReauthDialog
        open={openReauthDiscord}
        setOpen={setOpenReauthDiscord}
      />

      <ConnectDiscordDialog
        open={openConnectDiscord}
        setOpen={setOpenConnectDiscord}
      />
    </>
  )
}

function DiscordConnectMessage() {
  return (
    <div className="text-xs text-error-500">
      You need to{' '}
      <Link
        className="underline hover:text-error-600"
        href="/profile-manager/settings/connected-accounts"
      >
        connect your Discord account
      </Link>{' '}
      to your Grid Finder account.
    </div>
  )
}

function DiscordReauthMessage() {
  const pathname = usePathname()
  return (
    <div className="text-xs text-error-500">
      You need to{' '}
      <Button
        type="button"
        variant="underline"
        className="text-xs text-error-500 hover:text-error-600"
        onClick={() => signOut({ callbackUrl: pathname })}
      >
        re-authorise your Discord account
      </Button>{' '}
      to connect to a server.
    </div>
  )
}
